import { useCallback, useState, type FocusEvent } from 'react';
import clsx from 'clsx';
import type { TFunction } from 'i18next';
import type { CropUnit } from '../../state/historyStore';
import { COUNTRY_OPTIONS } from './cropPresets';

const UNIT_OPTIONS: CropUnit[] = ['cm', 'mm', 'in'];

interface CountryUnitSelectorProps {
  countryCode: string;
  unit: CropUnit;
  onCountryChange: (code: string) => void;
  onUnitChange: (unit: CropUnit) => void;
  t: TFunction;
}

export function CountryUnitSelector({
  countryCode,
  onCountryChange,
  onUnitChange,
  t,
  unit
}: CountryUnitSelectorProps) {
  const [countryMenuOpen, setCountryMenuOpen] = useState(false);
  const [unitMenuOpen, setUnitMenuOpen] = useState(false);

  const handleCountryBlur = useCallback((event: FocusEvent<HTMLDivElement>) => {
    const nextTarget = event.relatedTarget as Node | null;
    if (!event.currentTarget.contains(nextTarget)) {
      setCountryMenuOpen(false);
    }
  }, []);

  const handleUnitBlur = useCallback((event: FocusEvent<HTMLDivElement>) => {
    const nextTarget = event.relatedTarget as Node | null;
    if (!event.currentTarget.contains(nextTarget)) {
      setUnitMenuOpen(false);
    }
  }, []);

  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div
        className="flex flex-col"
        onMouseEnter={() => setCountryMenuOpen(true)}
        onMouseLeave={() => setCountryMenuOpen(false)}
        onFocusCapture={() => setCountryMenuOpen(true)}
        onBlurCapture={handleCountryBlur}
      >
        <button
          type="button"
          className="flex items-center gap-2 rounded-full border border-ai-surface-glow/30 bg-ai-surface-elevated/60 px-2 py-1 text-xs font-semibold text-ai-text-primary transition hover:border-ai-primary/40 focus:outline-none focus-visible:ring focus-visible:ring-ai-focus"
          onClick={() => setCountryMenuOpen((state) => !state)}
          aria-haspopup="menu"
          aria-expanded={countryMenuOpen}
          aria-label={t(`history.crop.countries.${countryCode.toLowerCase()}`)}
        >
          <FlagIcon code={countryCode} />
          <DisclosureChevron open={countryMenuOpen} />
          <span className="sr-only">{t(`history.crop.countries.${countryCode.toLowerCase()}`)}</span>
        </button>
        <div
          className={clsx(
            'flex flex-col gap-1 overflow-hidden rounded-2xl border border-ai-surface-glow/20 bg-ai-surface-elevated/90 text-xs shadow-ai-card transition-all duration-200 ease-out',
            countryMenuOpen
              ? 'pointer-events-auto mt-2 max-h-56 opacity-100 p-2'
              : 'pointer-events-none max-h-0 border-transparent opacity-0'
          )}
          role="menu"
        >
          {COUNTRY_OPTIONS.map((country) => (
            <button
              type="button"
              key={country.code}
              className={clsx(
                'flex items-center justify-center rounded-xl border p-1 transition hover:border-ai-primary/40 hover:bg-ai-primary/5 focus:outline-none focus-visible:ring focus-visible:ring-ai-focus',
                country.code === countryCode
                  ? 'border-ai-primary bg-ai-primary/10'
                  : 'border-ai-surface-glow/30 bg-ai-surface'
              )}
              onClick={() => onCountryChange(country.code)}
              role="menuitemradio"
              aria-checked={country.code === countryCode}
            >
              <FlagIcon code={country.code} />
              <span className="sr-only">{t(`history.crop.countries.${country.code.toLowerCase()}`)}</span>
            </button>
          ))}
        </div>
      </div>
      <div
        className="flex flex-col"
        onMouseEnter={() => setUnitMenuOpen(true)}
        onMouseLeave={() => setUnitMenuOpen(false)}
        onFocusCapture={() => setUnitMenuOpen(true)}
        onBlurCapture={handleUnitBlur}
      >
        <button
          type="button"
          className="flex items-center gap-2 rounded-full border border-ai-surface-glow/30 bg-ai-surface-elevated/60 px-3 py-1 text-xs font-semibold text-ai-text-primary transition hover:border-ai-primary/40 focus:outline-none focus-visible:ring focus-visible:ring-ai-focus"
          onClick={() => setUnitMenuOpen((state) => !state)}
          aria-haspopup="menu"
          aria-expanded={unitMenuOpen}
          aria-label={t(`history.crop.units.${unit}`)}
        >
          <UnitBadge label={t(`history.crop.units.${unit}`)} active />
          <DisclosureChevron open={unitMenuOpen} />
          <span className="sr-only">{t(`history.crop.units.${unit}`)}</span>
        </button>
        <div
          className={clsx(
            'flex flex-col gap-1 overflow-hidden rounded-2xl border border-ai-surface-glow/20 bg-ai-surface-elevated/90 text-xs shadow-ai-card transition-all duration-200 ease-out',
            unitMenuOpen
              ? 'pointer-events-auto mt-2 max-h-40 opacity-100 p-2'
              : 'pointer-events-none max-h-0 border-transparent opacity-0'
          )}
          role="menu"
        >
          {UNIT_OPTIONS.map((unitOption) => (
            <button
              type="button"
              key={unitOption}
              className="flex items-center justify-center rounded-xl border border-transparent p-1 transition hover:border-ai-primary/40 focus:outline-none focus-visible:ring focus-visible:ring-ai-focus"
              onClick={() => onUnitChange(unitOption)}
              role="menuitemradio"
              aria-checked={unit === unitOption}
            >
              <UnitBadge label={t(`history.crop.units.${unitOption}`)} active={unit === unitOption} />
              <span className="sr-only">{t(`history.crop.units.${unitOption}`)}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function FlagIcon({ code }: { code: string }) {
  const symbol =
    {
      CN: '🇨🇳',
      JP: '🇯🇵',
      US: '🇺🇸'
    }[code] ?? '🇨🇳';
  return (
    <span
      aria-hidden="true"
      className="inline-flex h-6 w-8 items-center justify-center rounded-md border border-ai-surface-glow/30 bg-white text-lg leading-none shadow-inner"
    >
      {symbol}
    </span>
  );
}

function UnitBadge({ label, active }: { label: string; active?: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={clsx(
        'inline-flex h-6 min-w-[2.5rem] items-center justify-center rounded-md border px-2 text-xs font-semibold transition',
        active ? 'border-ai-primary bg-ai-primary/10 text-ai-primary' : 'border-ai-surface-glow/30 text-ai-text-secondary'
      )}
    >
      {label}
    </span>
  );
}

function DisclosureChevron({ open }: { open: boolean }) {
  return (
    <svg
      aria-hidden="true"
      className={clsx('h-3 w-3 text-ai-text-secondary transition-transform duration-200', open ? 'rotate-180' : '')}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M4 6l4 4 4-4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
